import * as Sentry from '@sentry/react'

// T4.QA.2.4 (cross-repo follow-on, 2026-04-28): per-app DSN with legacy
// fallback. Veterans Shop handles veteran shipping addresses + Stripe checkout
// metadata — scrub mirrors veterans-web (SSN + DD214 covered by parent
// platform; included here defensively in case any cross-app error context
// includes a flagged identifier).
// Vite SPA runtime: env comes from import.meta.env (process is undefined in
// the browser bundle — referencing process.env here throws and crashes the
// React tree, which is why mounting ClientConsent previously broke the app).
const dsn =
  import.meta.env.VITE_SENTRY_DSN_VETERANS_SHOP ??
  import.meta.env.VITE_SENTRY_DSN

const SSN_DASHED_RE = /\b\d{3}-\d{2}-\d{4}\b/g
const SSN_LABELED_RE = /\bSSN[: ]\s*\d{9}\b/gi
const DD214_RE = /\bDD-?214\b[^\n\r]{0,80}/gi

function scrubString(s: string): string {
  return s
    .replace(SSN_LABELED_RE, '[ssn_redacted]')
    .replace(SSN_DASHED_RE, '[ssn_redacted]')
    .replace(DD214_RE, '[dd214_redacted]')
}

function redactRegulated(value: unknown): unknown {
  if (value === null) return value
  if (typeof value === 'string') return scrubString(value)
  if (typeof value !== 'object') return value
  if (Array.isArray(value)) return value.map(redactRegulated)
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    const key = k.toLowerCase()
    if (
      key.includes('password') ||
      key.includes('token') ||
      key.includes('secret') ||
      key.includes('email') ||
      key === 'authorization' ||
      key === 'cookie' ||
      key === 'ssn' ||
      key === 'social_security' ||
      key === 'social_security_number' ||
      key === 'dd214' ||
      key === 'dd_214' ||
      key === 'discharge_papers' ||
      key === 'file_number' ||
      key === 'va_file_number' ||
      key === 'dob' ||
      key === 'date_of_birth' ||
      key === 'card_number' ||
      key === 'cvv'
    ) {
      out[k] = '[redacted]'
    } else {
      out[k] = redactRegulated(v)
    }
  }
  return out
}

// W2.1 P0 fix (S9.A.01 Finding 2):
// GDPR Art. 7(3) + ePrivacy Art. 5(3) require analytics-class processing
// (Sentry session replays, browser tracing) to be gated on prior consent.
// Previously Sentry.init ran unconditionally at module load. We now expose
// a lazy initializer; ClientConsent invokes it ONLY when the user's
// consent.analytics === true. If consent is later withdrawn, callers can
// invoke `closeSentry()` to flush + shut down the client.

let sentryInitialized = false

export function initSentryIfConsented(analyticsConsent: boolean): void {
  // No-op when: no DSN configured · test env · already initialized · no consent
  if (sentryInitialized) return
  if (!analyticsConsent) return
  if (!dsn) return
  if (import.meta.env.MODE === 'test') return

  const isProd = import.meta.env.PROD
  Sentry.init({
    dsn,
    sampleRate: isProd ? 0.1 : 1.0,
    tracesSampleRate: isProd ? 0.05 : 1.0,
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    integrations: [Sentry.replayIntegration()],
    enabled: isProd || !!dsn,
    initialScope: {
      tags: {
        app: 'veterans-shop',
        surface: 'commerce',
      },
    },
    beforeSend(event) {
      if (event.user) {
        delete event.user.ip_address
        delete event.user.email
        delete event.user.username
      }
      if (typeof event.message === 'string') {
        event.message = scrubString(event.message)
      }
      if (event.exception?.values) {
        event.exception.values = event.exception.values.map((ex) => ({
          ...ex,
          value: typeof ex.value === 'string' ? scrubString(ex.value) : ex.value,
        }))
      }
      if (event.extra) {
        event.extra = redactRegulated(event.extra) as typeof event.extra
      }
      if (event.contexts) {
        event.contexts = redactRegulated(event.contexts) as typeof event.contexts
      }
      return event
    },
  })

  sentryInitialized = true
}

export async function closeSentry(): Promise<void> {
  if (!sentryInitialized) return
  await Sentry.close(2000)
  sentryInitialized = false
}

// NOTE: We intentionally do NOT call Sentry.init at module load.
// All initialization flows through initSentryIfConsented(), driven by
// the ConsentProvider state in ClientConsent.tsx.
